import WarningIcon from '@mui/icons-material/Warning';
import { Box } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { getOrderPatientFullName } from 'src/distribution_platforms/features/utils/patient_utils';
import type { WorkflowDefinition } from 'src/distribution_platforms/features/workflow_definition';
import { toOrderListColumns } from 'src/distribution_platforms/features/workflow_definition';
import {
  LdtOrderListItemFieldsFragment,
  SortBy,
  SpecimenType,
} from 'src/types';
import { SpecimenTypeUtils } from 'src/utils';

export enum OrderListColumns {
  Id = 'ORDER_ID',
  TempusId = 'TEMPUS_ID',
  Type = 'SPECIMEN TYPE',
  Patient = 'PATIENT',
  OrderingPhysician = 'ORDERING_PHYSICIAN',
  SubmissionDate = 'SUBMISSION_DATE',
  Status = 'STATUS',
}

const columnHelper = createColumnHelper<LdtOrderListItemFieldsFragment>();

/**
 * Builds the shared order list column set used by clinic, ancillary_lab, and direct_upload_lab.
 * Tempus uses its own implementation.
 */
export function buildOrderListColumns(workflow: WorkflowDefinition) {
  const hasMultipleSpecimenTypes =
    SpecimenTypeUtils.hasMultipleSpecimenTypesEnabled(
      workflow.supportedSpecimenTypes,
    );

  return toOrderListColumns([
    columnHelper.accessor(
      (order: LdtOrderListItemFieldsFragment) => order.crossOrgId,
      {
        id: OrderListColumns.Id,
        header: 'ORDER ID#',
        minSize: 180,
        maxSize: 200,
        meta: {
          isPrimaryColor: true,
        },
        cell: (info) => {
          const orderId = info.getValue();
          const needsAttention = info.row.original.needsAttention === true;
          return (
            <Box
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <WarningIcon
                sx={{
                  color: 'warning.main',
                  fontSize: 18,
                  visibility: needsAttention ? 'visible' : 'hidden',
                }}
                aria-label={needsAttention ? 'Needs attention' : undefined}
                aria-hidden={!needsAttention}
              />
              {orderId}
            </Box>
          );
        },
      },
    ),
    ...(hasMultipleSpecimenTypes
      ? [
          columnHelper.accessor(
            (order: LdtOrderListItemFieldsFragment) => order.specimenType,
            {
              id: OrderListColumns.Type,
              header: 'SPECIMEN TYPE',
              minSize: 220,
              maxSize: 220,
              cell: (info) =>
                info.getValue()
                  ? SpecimenTypeUtils.getSpecimenTypeName(
                      info.getValue() as SpecimenType,
                    )
                  : '',
            },
          ),
        ]
      : []),
    columnHelper.accessor(
      (order: LdtOrderListItemFieldsFragment) => getOrderPatientFullName(order),
      {
        id: OrderListColumns.Patient,
        header: 'PATIENT',
        minSize: 220,
        maxSize: 260,
        size: 260,
        cell: (info) => info.getValue() || '',
      },
    ),
    columnHelper.accessor(
      (order: LdtOrderListItemFieldsFragment) => {
        const orderingProvider = order.orderingProvider;
        if (!orderingProvider?.orderingPhysician) return null;
        const op = orderingProvider.orderingPhysician;
        return [op.firstName, op.lastName].filter(Boolean).join(' ');
      },
      {
        id: OrderListColumns.OrderingPhysician,
        header: 'ORDERING PHYSICIAN',
        minSize: 220,
        maxSize: 260,
        size: 260,
        cell: (info) => info.getValue() || '',
      },
    ),
    columnHelper.accessor('submittedAt', {
      id: OrderListColumns.SubmissionDate,
      header: 'SUBMISSION DATE',
      minSize: 200,
      maxSize: 200,
      enableSorting: true,
      sortingFn: 'datetime',
      meta: { sortBy: SortBy.SubmittedAt },
      cell: (info) =>
        info.getValue() ? new Date(info.getValue()!).toLocaleDateString() : '',
    }),
    columnHelper.accessor(
      (order: LdtOrderListItemFieldsFragment) => order.status,
      {
        id: OrderListColumns.Status,
        header: 'STATUS',
        minSize: 180,
        maxSize: 180,
        size: 180,
        enableSorting: false,
        cell: (info) => workflow.getStatusDisplayName(info.getValue()),
      },
    ),
  ]);
}
